import CategoryIcon from "@mui/icons-material/Category";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import PlaceIcon from "@mui/icons-material/Place";
import { Box, Container, Typography } from "@mui/material";
import { getSiteStats } from "../utils/stats";
import { useCountUpOnView } from "../utils/useCountUpOnView";

const StatCard = ({ label, value, icon, suffix = "+" }) => {
  const { ref, value: animated } = useCountUpOnView(value);

  return (
    <Box
      ref={ref}
      sx={{
        px: 4,
        py: 4,
        borderRadius: 2,
        textAlign: "center",
        background: "linear-gradient(180deg, var(--surface-light), #ffffff)",
        border: "1px solid rgba(13,59,102,0.06)",
        boxShadow: "0 24px 60px rgba(13,59,102,0.08)",
        transition: "all 0.35s ease",
        "&:hover": {
          transform: "translateY(-6px)",
          boxShadow: "0 40px 90px rgba(13,59,102,0.12)",
        },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
      }}
    >
      {/* Icon */}
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: "50%",
          mb: 2,

          display: "flex",
          alignItems: "center",
          justifyContent: "center",

          bgcolor: "var(--color-bliss)",
          color: "var(--color-neon)",
          boxShadow: "0 8px 20px rgba(244,211,94,0.35)",
        }}
      >
        {icon}
      </Box>

      {/* Number */}
      <Typography
        sx={{
          fontSize: { xs: "2.3rem", md: "2.9rem" },
          fontWeight: 900,
          color: "var(--color-neon)",
          letterSpacing: "-0.02em",
          lineHeight: 1.05,
        }}
      >
        {animated.toLocaleString()}
        {suffix}
      </Typography>

      {/* Label */}
      <Typography
        sx={{
          mt: 1,
          fontSize: "0.95rem",
          color: "var(--text-secondary)",
          fontWeight: 600,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

const StatsSection = () => {
  const { cities, categories, places } = getSiteStats();

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        py: { xs: 5, md: 7 },
        borderTop: "1px solid rgba(13,59,102,0.06)",
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box
          sx={{
            mb: { xs: 4, md: 6 },
            maxWidth: 520,
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "1.6rem", md: "2.1rem" },
              fontWeight: 900,
              color: "var(--text-heading)",
              letterSpacing: "-0.02em",
            }}
          >
            NearbyGuide{" "}
            <Box component="span" sx={{ color: "var(--color-bliss)" }}>
              at a Glance
            </Box>
          </Typography>

          <Typography
            sx={{
              mt: 1.4,
              fontSize: "0.95rem",
              color: "var(--color-muted)",
              lineHeight: 1.7,
            }}
          >
            A growing directory of trusted places — carefully curated and
            expanding across major Indian cities.
          </Typography>
        </Box>

        {/* Cards */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            },
            gap: { xs: 3, md: 4 },
          }}
        >
          <StatCard
            label="Cities Covered"
            value={cities}
            icon={<LocationCityIcon fontSize="small" />}
          />

          <StatCard
            label="Categories"
            value={categories}
            icon={<CategoryIcon fontSize="small" />}
          />

          <StatCard
            label="Places Listed"
            value={places}
            icon={<PlaceIcon fontSize="small" />}
          />
        </Box>
      </Container>
    </Box>
  );
};

export default StatsSection;
